import type { TFunction } from 'i18next';

const KNOWN = new Set([
  'PLACE_NOT_ADMINISTERED',
  'USER_NOT_IN_PLACE',
  'ROLE_ASSIGNMENT_NOT_FOUND',
  'INVITATION_NOT_FOUND',
  'ROLE_NOT_ASSIGNABLE',
  'PERMISSION_DENIED',
  'ACTION_NOT_ALLOWED_AT_TIER',
  'ROLE_ASSIGNMENT_ALREADY_GRANTED',
  'ROLE_ASSIGNMENT_ALREADY_REVOKED',
  'LAST_OPERATOR_GRANT_PROTECTED',
  'INVITATION_ALREADY_PENDING',
  'INVITATION_NOT_PENDING',
  'INVITATION_NOT_RENEWABLE',
  'MEMBER_TAG_REQUIRED',
  'INVALID_CURSOR',
  'INVALID_QUERY',
  'USER_NOT_FOUND',
  'USER_DEACTIVATED',
  'SELF_DEACTIVATION_REFUSED',
  'ACTIVE_CONTEXT_REQUIRED',
  'ROLE_NOT_FOUND',
  'MODULE_NOT_FOUND',
  'MODULE_ALREADY_ACTIVE',
  'MODULE_NOT_ACTIVE_HERE',
  'MODULE_NEEDS_ORGANIZATION',
  // Slice 2: Tenant and Organizzazioni.
  'TENANT_NAME_TAKEN',
  'TENANT_NOT_FOUND',
  'ORGANIZATION_NAME_TAKEN',
  'ORGANIZATION_NOT_FOUND',
  'ORGANIZATION_ALREADY_ARCHIVED',
  'ORGANIZATION_NOT_ARCHIVED',
  'LAST_ACTIVE_ORGANIZATION_PROTECTED',
  'ACTIVE_CONTEXT_ARCHIVAL_REFUSED',
]);

/** The `code` of a problem-details body, when the server sent one. */
export function problemCode(error: unknown): string | null {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = error.code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/**
 * A failure in plain Italian (FR-UX-4): never the server's own text, never a status number. A
 * known code says what happened — including "this left the place while you looked", which is not
 * a lost right — and anything else says that nothing changed and that a retry is safe.
 */
export function problemMessage(
  t: TFunction<'administration'>,
  error: unknown,
  status?: number,
): string {
  const code = problemCode(error);
  if (code === 'REMOVAL_INTERRUPTED') {
    const revoked =
      typeof error === 'object' && error !== null && 'revoked' in error ? Number(error.revoked) : 0;
    return t('problems.REMOVAL_INTERRUPTED', { count: Number.isFinite(revoked) ? revoked : 0 });
  }
  if (code !== null && KNOWN.has(code)) {
    return t(`problems.${code}` as 'problems.generic');
  }
  if (status !== undefined && status >= 500) {
    return t('problems.unavailable');
  }
  return t('problems.generic');
}

/**
 * Refusals that say what the screen shows is no longer true — the caller's standing changed after
 * the reads, the person or the grant left the place, a removal stopped part-way. The screen reads
 * again after saying so, rather than leaving a button that will be refused a second time.
 */
const STALE = new Set([
  'ACTION_NOT_ALLOWED_AT_TIER',
  'REMOVAL_INTERRUPTED',
  'USER_NOT_IN_PLACE',
  'ROLE_ASSIGNMENT_NOT_FOUND',
  'ROLE_ASSIGNMENT_ALREADY_REVOKED',
  'INVITATION_NOT_FOUND',
  'INVITATION_NOT_PENDING',
  'MODULE_NOT_FOUND',
  'MODULE_ALREADY_ACTIVE',
  'MODULE_NOT_ACTIVE_HERE',
  // A tenant that went while the list was open.
  'TENANT_NOT_FOUND',
  // An organization archived, restored or moved while the list was open.
  'ORGANIZATION_NOT_FOUND',
  'ORGANIZATION_ALREADY_ARCHIVED',
  'ORGANIZATION_NOT_ARCHIVED',
  'LAST_ACTIVE_ORGANIZATION_PROTECTED',
  'ACTIVE_CONTEXT_ARCHIVAL_REFUSED',
]);

export function isStale(error: unknown): boolean {
  const code = problemCode(error);
  return code !== null && STALE.has(code);
}

/** The caller's own standing changed: the place's `can` must be read again, not just the list. */
export function standingChanged(error: unknown): boolean {
  return problemCode(error) === 'ACTION_NOT_ALLOWED_AT_TIER';
}
